import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  StethoscopeIcon,
  ArrowRightIcon,
  StackIcon,
  ShieldCheckIcon,
  SpinnerGapIcon,
} from "@phosphor-icons/react";
import { getModules, createSession } from "../api";
import { toast } from "sonner";

const PAYER_TAG = {
  Medicare: "bg-[#0a0a0a] text-white",
  Medicaid: "bg-[#e11d48] text-white",
};

export default function StartSession() {
  const [modules, setModules] = useState([]);
  const [name, setName] = useState(() => localStorage.getItem("claimscope_trainee") || "");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    getModules().then(setModules).catch(() => toast.error("Failed to load modules"));
  }, []);

  const start = async () => {
    if (!name.trim()) return toast.error("Enter your trainee name to begin");
    if (!selected) return toast.error("Select a training module");
    setLoading(true);
    try {
      localStorage.setItem("claimscope_trainee", name.trim());
      const session = await createSession(name.trim(), selected);
      navigate(`/simulator/${session.id}`);
    } catch (e) {
      toast.error("Could not start session");
      setLoading(false);
    }
  };

  const signOut = () => {
    localStorage.removeItem("claimscope_trainee");
    setName("");
    toast.success("Signed out — enter a new trainee name");
  };

  const returning = !!localStorage.getItem("claimscope_trainee");

  return (
    <div className="grid-bg min-h-[calc(100vh-4rem)]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-12 md:py-16">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mb-14"
        >
          <div className="label mb-4 flex items-center gap-2">
            <ShieldCheckIcon size={15} weight="bold" /> NCD / LCD Compliance Training
          </div>
          <h1 className="font-head font-black text-5xl md:text-6xl tracking-tighter leading-[0.95]">
            Adjudicate claims.<br />
            <span className="text-[#525252]">Build QA accuracy.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#525252] max-w-xl leading-relaxed">
            Review mock Medicare &amp; Medicaid claims against real coverage rules.
            Decide Approve, Deny, or Pend — and learn instantly when you miss.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-[1fr_360px] gap-8">
          {/* Module selection */}
          <div>
            <div className="label mb-4 flex items-center gap-2">
              <StackIcon size={15} weight="bold" /> Select a Module ({modules.length})
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {modules.map((m, i) => {
                const active = selected === m.id;
                return (
                  <motion.button
                    key={m.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + i * 0.07 }}
                    onClick={() => setSelected(m.id)}
                    data-testid={`module-card-${m.id}`}
                    className={`text-left p-5 border transition-all ${
                      active
                        ? "border-[#0a0a0a] bg-white -translate-y-0.5 shadow-[6px_6px_0_0_#0a0a0a]"
                        : "border-[#e5e5e5] bg-white hover:border-[#0a0a0a]"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span
                        className={`text-[0.62rem] font-bold uppercase tracking-widest px-2 py-1 ${PAYER_TAG[m.payer]}`}
                      >
                        {m.payer}
                      </span>
                      <span className="label text-[0.6rem]">{m.difficulty}</span>
                    </div>
                    <div className="font-head font-bold text-lg tracking-tight leading-snug mb-2">
                      {m.title}
                    </div>
                    <p className="text-sm text-[#525252] leading-relaxed mb-4">
                      {m.description}
                    </p>
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-[#0a0a0a]">
                      <StethoscopeIcon size={15} weight="bold" />
                      {m.claim_count} claims to review
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </div>

          {/* Start panel */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="lg:sticky lg:top-24 h-fit border-2 border-[#0a0a0a] bg-white"
          >
            <div className="bg-[#0a0a0a] px-5 py-3">
              <div className="label text-white/70 text-[0.6rem]">Begin Session</div>
              <div className="font-head font-black text-white text-xl tracking-tight">
                Trainee Check-In
              </div>
            </div>
            <div className="p-5 space-y-5">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="label">Trainee Name</label>
                  {returning && (
                    <button
                      type="button"
                      onClick={signOut}
                      data-testid="sign-out-btn"
                      className="text-[0.62rem] font-bold uppercase tracking-widest text-[#e11d48] hover:underline"
                    >
                      Not you? Switch
                    </button>
                  )}
                </div>
                {returning && (
                  <div
                    className="mb-2 text-xs font-medium text-[#10b981]"
                    data-testid="returning-trainee-banner"
                  >
                    Welcome back, {name} — pick a module to continue.
                  </div>
                )}
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && start()}
                  placeholder="e.g. Jordan Vega"
                  data-testid="trainee-name-input"
                  className="w-full border border-[#e5e5e5] px-3.5 py-3 text-sm font-medium focus:outline-none focus:border-[#0a0a0a] focus:ring-2 focus:ring-[#0a0a0a]/10"
                />
              </div>
              <div className="text-xs text-[#525252] border border-[#e5e5e5] bg-[#f8f9fa] p-3">
                {selected ? (
                  <span>
                    Module selected:{" "}
                    <span className="font-semibold text-[#0a0a0a]">
                      {modules.find((m) => m.id === selected)?.title}
                    </span>
                  </span>
                ) : (
                  "No module selected yet — pick one from the left."
                )}
              </div>
              <button
                onClick={start}
                disabled={loading}
                data-testid="start-session-btn"
                className="w-full bg-[#0a0a0a] text-white font-semibold py-3.5 flex items-center justify-center gap-2 hover:bg-[#262626] transition-colors disabled:opacity-60"
              >
                {loading ? (
                  <SpinnerGapIcon size={18} weight="bold" className="animate-spin" />
                ) : (
                  <>
                    Launch Simulator <ArrowRightIcon size={17} weight="bold" />
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
